const MAX_DIMENSION = 500
const MAX_FILE_SIZE = 800 * 1024 // 800KB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const COMPRESSION_QUALITY = 0.9

export interface ImageValidationResult {
  valid: boolean
  error?: string
}

export function validateImageFile(file: File): ImageValidationResult {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Formato no soportado. Usa JPG, PNG o WebP.',
    }
  }

  if (file.size > 8 * 1024 * 1024) {
    // 8MB max for raw file before compression
    return {
      valid: false,
      error: 'La imagen es demasiado grande. Maximo 8MB.',
    }
  }

  return { valid: true }
}

export async function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('No se pudo crear el contexto de canvas'))
      return
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          height = Math.round((height * MAX_DIMENSION) / width)
          width = MAX_DIMENSION
        } else {
          width = Math.round((width * MAX_DIMENSION) / height)
          height = MAX_DIMENSION
        }
      }

      canvas.width = width
      canvas.height = height

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height)

      // Try to get a small enough result
      let quality = COMPRESSION_QUALITY
      let dataUrl = canvas.toDataURL('image/jpeg', quality)

      // Reduce quality until we're under the size limit
      while (dataUrl.length > MAX_FILE_SIZE && quality > 0.3) {
        quality -= 0.1
        dataUrl = canvas.toDataURL('image/jpeg', quality)
      }

      if (dataUrl.length > MAX_FILE_SIZE) {
        reject(new Error('No se pudo comprimir la imagen lo suficiente'))
        return
      }

      resolve(dataUrl)
    }

    img.onerror = () => {
      reject(new Error('No se pudo cargar la imagen'))
    }

    // Create object URL from file and load
    const objectUrl = URL.createObjectURL(file)
    img.src = objectUrl
  })
}

export function getAcceptedImageTypes(): Record<string, string[]> {
  return {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
  }
}
